
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Table, TableBody } from "@/components/ui/table";
import { QRCodeRow } from "./QRCodeRow";
import { QRCodesTableHeader } from "./QRCodesTableHeader";

type QRCodeStatus = {
  token: string;
  created_at: string;
  assigned_group: 'intervention' | 'control' | null;
  responses: {
    response_number: number;
    created_at: string | null;
    phone_number: string | null;
  }[];
};

interface QRCodesListProps {
  groupId?: string;
}

export const QRCodesList = ({ groupId }: QRCodesListProps = {}) => {
  const { data: qrCodes, isLoading, refetch } = useQuery({
    queryKey: ['qrCodes', groupId],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("No session found");

      let query = supabase
        .from("auth_tokens")
        .select(`
          token,
          created_at,
          assigned_group
        `)
        .order('created_at', { ascending: false });

      if (groupId) {
        query = query.eq('group_id', groupId);
      } else {
        query = query.eq('created_by', session.user.id);
      }

      const { data: tokens, error: tokensError } = await query;

      if (tokensError) throw tokensError;

      // Get progression data for all tokens
      const { data: progressions, error: progressionError } = await supabase
        .from("token_progression")
        .select("token, response_count")
        .in('token', tokens?.map(t => t.token) || []);

      if (progressionError) throw progressionError;

      // Get survey responses with phone numbers (only for authenticated group leaders)
      const { data: responses, error: responsesError } = await supabase
        .from("survey_responses")
        .select("auth_token, response_number, created_at, phone_number")
        .in('auth_token', tokens?.map(t => t.token) || []);

      if (responsesError) throw responsesError;

      // Combine the data
      return tokens.map((token) => {
        const tokenProgression = progressions?.find(p => p.token === token.token);
        const tokenResponses = responses?.filter(r => r.auth_token === token.token) || [];
        
        return {
          token: token.token,
          created_at: token.created_at,
          assigned_group: token.assigned_group,
          responses: tokenResponses.map(r => ({
            response_number: r.response_number,
            created_at: r.created_at,
            phone_number: r.phone_number
          })),
        };
      }) as QRCodeStatus[];
    },
    refetchOnWindowFocus: true,
    staleTime: 0,
    refetchInterval: 30000, // Refetch every 30 seconds
    gcTime: 0,
  });

  // Set up real-time subscription for auth_tokens and survey_responses updates
  useEffect(() => {
    const channel = supabase
      .channel('qr-codes-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'auth_tokens'
        },
        () => refetch()
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'survey_responses'
        },
        () => refetch()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [refetch]);

  if (isLoading) {
    return <div className="text-muted-foreground">Laddar QR-koder...</div>;
  }

  if (!qrCodes?.length) {
    return <div className="text-muted-foreground">Inga QR-koder har genererats än.</div>;
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-medium">Genererade QR-koder</h2>
      <div className="w-full overflow-x-auto">
        <Table className="min-w-full">
          <QRCodesTableHeader />
          <TableBody>
            {qrCodes.map((qrCode) => (
              <QRCodeRow key={qrCode.token} {...qrCode} />
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
